import { Component } from "@theme/component";
import { sectionRenderer } from "@theme/section-renderer";
import { requestIdleCallback, viewTransition } from "@theme/utilities";
import { ThemeEvents } from "@theme/events";
import { PaginatedListAspectRatioHelper } from "@theme/paginated-list-aspect-ratio";

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #isRenderingNextPage = false;

  /** @type {boolean} */
  #isRenderingPreviousPage = false;

  /** @type {number} */
  #lastRenderTime = 0;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage("next");
    this.#fetchPage("previous");
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate,
    );

    // Reset rendering states
    this.#isRenderingNextPage = false;
    this.#isRenderingPreviousPage = false;
  }

  #observeViewMore() {
    // Get all elements and only observe the first of each type to avoid duplicates
    const viewMorePreviousElements = this.querySelectorAll(
      '[ref="viewMorePrevious"]',
    );
    const viewMoreNextElements = this.querySelectorAll('[ref="viewMoreNext"]');

    const viewMorePrevious = viewMorePreviousElements[0];
    const viewMoreNext = viewMoreNextElements[0];

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              // Simple cooldown to prevent rapid-fire renders
              const now = Date.now();
              if (now - this.#lastRenderTime < 1000) {
                return;
              }

              if (entry.target === viewMorePrevious) {
                this.#lastRenderTime = now;
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#lastRenderTime = now;
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: "100px",
        },
      );
    }

    // Check current page boundaries to determine what to observe
    const { grid, cards } = this.refs;
    const currentPage =
      cards && cards.length > 0 ? Number(cards[0].dataset.page) : 1;
    const lastPage = grid ? Number(grid.dataset.lastPage) : 1;

    // Only observe viewMorePrevious if we're not at page 1
    if (viewMorePrevious && currentPage > 1) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    // Only observe viewMoreNext if we're not at the last page
    if (viewMoreNext && currentPage < lastPage) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage))
      return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === "next") {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    // Check if page is already cached
    if (this.pages.has(page.page)) {
      resolvePromise();
      return;
    }
    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set("page", pageNumber.toString());
      newUrl.hash = "";
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) {
      return;
    }

    const pageContent = await sectionRenderer.getSectionHTML(
      this.sectionId,
      true,
      pageInfo.url,
    );
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    // Prevent concurrent next page renders
    if (this.#isRenderingNextPage) {
      return;
    }

    const nextPage = this.#getPage("next");
    const lastPage = Number(grid.dataset.lastPage);

    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      return;
    }

    // Set rendering state
    this.#isRenderingNextPage = true;

    try {
      // If we're about to load the last page, unobserve viewMoreNext after this render
      const willReachLastPage = nextPage.page === lastPage;

      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage("next");

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper.processNewElements();

      // Force refresh refs after DOM changes
      this.#refreshRefs();

      history.pushState("", "", nextPage.url.toString());

      // If we just loaded the last page, unobserve viewMoreNext to prevent continuous firing
      if (willReachLastPage) {
        const { viewMoreNext } = this.refs;
        if (viewMoreNext && this.infinityScrollObserver) {
          this.infinityScrollObserver.unobserve(viewMoreNext);
        }
      }

      requestIdleCallback(() => {
        // Only prefetch if we haven't reached the last page
        if (!willReachLastPage) {
          this.#fetchPage("next");
        }
      });
    } catch (error) {
      console.error(
        "[PaginatedList] #renderNextPage - Error during render:",
        error,
      );
    } finally {
      // Always clear rendering state
      this.#isRenderingNextPage = false;
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    // Prevent concurrent previous page renders
    if (this.#isRenderingPreviousPage) {
      return;
    }

    const previousPage = this.#getPage("previous");

    if (!previousPage || !this.#shouldUsePage(previousPage)) {
      return;
    }

    // Set rendering state
    this.#isRenderingPreviousPage = true;

    try {
      // If we're about to load page 1, unobserve viewMorePrevious after this render
      // to prevent continuous firing at the top of the page
      const willReachPageOne = previousPage.page === 1;

      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage("previous");

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement
        ? firstElement.getBoundingClientRect().top + window.scrollY
        : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);

      this.#aspectRatioHelper.processNewElements();

      // Force refresh refs after DOM changes
      this.#refreshRefs();

      history.pushState("", "", previousPage.url.toString());

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight =
          firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: "instant",
        });
      }

      // If we just loaded page 1, unobserve viewMorePrevious to prevent continuous firing
      if (willReachPageOne) {
        const { viewMorePrevious } = this.refs;
        if (viewMorePrevious && this.infinityScrollObserver) {
          this.infinityScrollObserver.unobserve(viewMorePrevious);
        }
      }

      requestIdleCallback(() => {
        // Only prefetch if we haven't reached page 1
        if (!willReachPageOne) {
          console.log(
            "[PaginatedList] #renderPreviousPage - Prefetching previous page in idle callback",
          );
          this.#fetchPage("previous");
        } else {
          console.log(
            "[PaginatedList] #renderPreviousPage - At page 1, skipping prefetch",
          );
        }
      });
    } catch (error) {
      console.error(
        "[PaginatedList] #renderPreviousPage - Error during render:",
        error,
      );
    } finally {
      // Always clear rendering state
      this.#isRenderingPreviousPage = false;
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === "previous";

    if (!cards || cards.length === 0) {
      return;
    }

    const targetCard = isPrevious ? cards[0] : cards[cards.length - 1];
    if (!targetCard) {
      return;
    }
    const currentPage = Number(targetCard.dataset.page);
    const targetPage = isPrevious ? currentPage - 1 : currentPage + 1;

    if (targetPage < 1) {
      return;
    }

    const url = new URL(window.location.href);
    if (targetPage > 1) {
      url.searchParams.set("page", targetPage.toString());
    } else {
      url.searchParams.delete("page");
    }
    url.hash = "";

    return {
      page: targetPage,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) {
      return;
    }

    const parsedPage = new DOMParser().parseFromString(pageHTML, "text/html");
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    const items = gridElement.querySelectorAll(':scope > [ref="cards[]"]');
    return items;
  }

  /**
   * Force refresh of cached refs after DOM changes
   */
  #refreshRefs() {
    // Force the parent class to update refs immediately
    // This calls the private #updateRefs() method from Component class
    this.updatedCallback();
  }

  get sectionId() {
    const id = this.getAttribute("section-id");

    if (!id) throw new Error("The section-id attribute is required");
    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        // Re-observe everything since we have new content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage("next");
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ["data-last-page"],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
